import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../i18n";
import { Plus, Search, User, Edit2, Trash2, X } from "lucide-react";

const EMPTY = {
    codice_fiscale: "", cognome: "", nome: "", sesso: "M", data_nascita: "",
    comune_nascita: "", provincia_nascita: "", stato_nascita: "ITALIA", cittadinanza: "ITALIANA",
    email: "", telefono: "",
    comune_residenza: "", provincia_residenza: "", cap_residenza: "",
    indirizzo_residenza: "", civico_residenza: "",
    partita_iva: "", notes: "",
};

export default function Clients() {
    const { t } = useI18n();
    const [list, setList] = useState([]);
    const [search, setSearch] = useState("");
    const [editing, setEditing] = useState(null); // null | "new" | client object
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/clients");
            setList(r.data);
        } catch {}
    };

    useEffect(() => { load(); }, []);

    const onDelete = async (id) => {
        if (!window.confirm(t.clients.confirmDelete)) return;
        await api.delete(`/clients/${id}`);
        load();
    };

    const filtered = list.filter((c) => {
        const q = search.toLowerCase();
        return !q || (c.codice_fiscale || "").toLowerCase().includes(q)
            || (c.cognome || "").toLowerCase().includes(q)
            || (c.nome || "").toLowerCase().includes(q)
            || (c.partita_iva || "").toLowerCase().includes(q);
    });

    return (
        <div className="min-h-screen p-6 lg:p-10">
            <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-medium tracking-tight">{t.clients.title}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t.clients.subtitle}</p>
                </div>
                <button
                    data-testid="new-client-button"
                    onClick={() => setEditing("new")}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors"
                >
                    <Plus size={16} /> {t.clients.newClient}
                </button>
            </header>

            <div className="mb-6 max-w-md">
                <div className="relative">
                    <Search size={14} className="absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                        data-testid="clients-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t.clients.search}
                        className="w-full ps-9 pe-4 py-2.5 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm"
                    />
                </div>
            </div>

            <div className="rounded-lg border border-border bg-card overflow-hidden">
                {filtered.length === 0 ? (
                    <div className="p-12 text-center text-sm text-muted-foreground">{t.clients.empty}</div>
                ) : (
                    <div className="divide-y divide-border">
                        {filtered.map((c) => (
                            <div key={c.id} data-testid={`client-row-${c.id}`} className="px-5 py-4 flex items-center justify-between hover:bg-accent/30 transition-colors">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
                                        <User size={16} className="text-muted-foreground" />
                                    </div>
                                    <div className="min-w-0">
                                        <div className="font-medium text-sm truncate">{c.cognome} {c.nome}</div>
                                        <div className="text-xs text-muted-foreground truncate">CF: {c.codice_fiscale}{c.partita_iva && ` · P.IVA: ${c.partita_iva}`}</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button data-testid={`edit-client-${c.id}`} onClick={() => setEditing(c)} className="p-2 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors">
                                        <Edit2 size={14} />
                                    </button>
                                    <button data-testid={`delete-client-${c.id}`} onClick={() => onDelete(c.id)} className="p-2 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors">
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {editing && (
                <ClientDialog
                    client={editing === "new" ? null : editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </div>
    );
}

function ClientDialog({ client, onClose, onSaved }) {
    const { t } = useI18n();
    const [data, setData] = useState(client || EMPTY);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);

    const f = (k) => (e) => setData({ ...data, [k]: e.target.value });

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setErr("");
        try {
            if (client?.id) await api.put(`/clients/${client.id}`, data);
            else await api.post("/clients", data);
            onSaved();
        } catch (e) {
            setErr(e?.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-orange-900/20 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
            <div data-testid="client-dialog" className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-card rounded-lg border border-border shadow-xl">
                <div className="px-6 py-4 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
                    <h2 className="font-display text-xl font-medium">
                        {client?.id ? t.clients.editClient : t.clients.createClient}
                    </h2>
                    <button data-testid="close-client-dialog" onClick={onClose} className="p-1.5 rounded-md hover:bg-accent">
                        <X size={16} />
                    </button>
                </div>
                <form onSubmit={save} className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <Field label={t.clients.fields.codice_fiscale} required>
                        <input data-testid="client-cf" required disabled={!!client?.id} value={data.codice_fiscale} onChange={f("codice_fiscale")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.partita_iva}>
                        <input data-testid="client-piva" value={data.partita_iva} onChange={f("partita_iva")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.cognome} required>
                        <input data-testid="client-cognome" required value={data.cognome} onChange={f("cognome")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.nome} required>
                        <input data-testid="client-nome" required value={data.nome} onChange={f("nome")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.sesso}>
                        <select data-testid="client-sesso" value={data.sesso} onChange={f("sesso")} className={inputCls}>
                            <option value="M">M</option>
                            <option value="F">F</option>
                        </select>
                    </Field>
                    <Field label={t.clients.fields.data_nascita}>
                        <input data-testid="client-datanascita" type="date" value={data.data_nascita} onChange={f("data_nascita")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.comune_nascita}>
                        <input value={data.comune_nascita} onChange={f("comune_nascita")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.provincia_nascita}>
                        <input maxLength={2} value={data.provincia_nascita} onChange={f("provincia_nascita")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.email}>
                        <input type="email" value={data.email} onChange={f("email")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.telefono}>
                        <input value={data.telefono} onChange={f("telefono")} className={inputCls} />
                    </Field>
                    <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-12 gap-4">
                        <div className="sm:col-span-7"><Field label={t.clients.fields.indirizzo_residenza}><input value={data.indirizzo_residenza} onChange={f("indirizzo_residenza")} className={inputCls} /></Field></div>
                        <div className="sm:col-span-2"><Field label={t.clients.fields.civico_residenza}><input value={data.civico_residenza} onChange={f("civico_residenza")} className={inputCls} /></Field></div>
                        <div className="sm:col-span-3"><Field label={t.clients.fields.cap_residenza}><input maxLength={5} value={data.cap_residenza} onChange={f("cap_residenza")} className={inputCls} /></Field></div>
                    </div>
                    <Field label={t.clients.fields.comune_residenza}>
                        <input value={data.comune_residenza} onChange={f("comune_residenza")} className={inputCls} />
                    </Field>
                    <Field label={t.clients.fields.provincia_residenza}>
                        <input maxLength={2} value={data.provincia_residenza} onChange={f("provincia_residenza")} className={inputCls} />
                    </Field>
                    <div className="sm:col-span-2">
                        <Field label={t.clients.fields.notes}>
                            <textarea rows={2} value={data.notes} onChange={f("notes")} className={inputCls} />
                        </Field>
                    </div>
                    {err && <div className="sm:col-span-2 text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">{err}</div>}
                    <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
                        <button type="button" onClick={onClose} className="px-4 py-2 rounded-md hover:bg-accent text-sm font-medium transition-colors">{t.clients.cancel}</button>
                        <button data-testid="save-client" type="submit" disabled={saving} className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 text-sm font-medium transition-colors disabled:opacity-50">{saving ? "..." : t.clients.save}</button>
                    </div>
                </form>
            </div>
        </div>
    );
}

const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";

function Field({ label, children, required }) {
    return (
        <label className="block">
            <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-muted-foreground mb-1 block">{label}{required && <span className="text-primary ms-1">*</span>}</span>
            {children}
        </label>
    );
}
